import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Icon, Radio, Row, Select, Spin, Table, message } from 'antd';
import { withRouter } from 'react-router-dom';
import MonthPicker from 'components/MonthPicker/MonthPicker';
import { MONTH_FORMAT } from 'utils/constants';
import {
  auditReporting,
  autoCalculateReporting,
  getReportPdf,
  listReports,
  listServicePackages,
  setPaymentStatus,
  unauditReporting,
} from '../api/ownerReporting';
import { loadHostProperties, loadUnitsForProperty } from '../api/hostListings';
import { useOwnerReportingHost } from '../hooks/useOwnerReportingHost';
import { readPageFilters, toMonthDate, useSyncPageFilters } from './pageFilters';
import { findPackageById, packagesForProperty, unitsForPackage } from './reportingPackageFilter';
import { downloadCsvFile } from './revenueCsv';
import { mapOwnerHostProfitCsvRows } from './auditedExportCsv';
import BulkAuditWizard from '../components/BulkAuditWizard';
import BulkPaidWizard from '../components/BulkPaidWizard';
import ReportingCard from '../components/ReportingCard';
import { mapReportToCard } from './payoutV2/mapReportToCard';
import styles from './Reporting.module.css';

const ReportingPage = ({ location, history }) => {
  const { hostId } = useOwnerReportingHost();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [bulkAuditVisible, setBulkAuditVisible] = useState(false);
  const [bulkPaidVisible, setBulkPaidVisible] = useState(false);
  const [properties, setProperties] = useState([]);
  const [packages, setPackages] = useState([]);
  const [units, setUnits] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitNameById, setUnitNameById] = useState({});
  const [filters, setFilters] = useState(() => {
    const initial = readPageFilters(location && location.search);
    return {
      propertyId: initial.propertyId,
      servicePackageId: initial.servicePackageId,
      unitId: initial.unitId,
      year: initial.monthDate.year(),
      month: initial.monthDate.month() + 1,
      monthDate: initial.monthDate,
      view: initial.view,
    };
  });

  const load = async (
    year = filters.year,
    month = filters.month,
    propertyId = filters.propertyId,
    unitId = filters.unitId,
    servicePackageId = filters.servicePackageId
  ) => {
    if (!hostId || !propertyId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      await autoCalculateReporting(hostId, {
        year,
        month,
        propertyId,
        unitId: unitId || undefined,
      });
      const params = { year, month };
      if (unitId) {
        params.unit = unitId;
      } else {
        params.property = propertyId;
      }
      if (servicePackageId) params.servicePackage = servicePackageId;
      setRows((await listReports(hostId, params)) || []);
    } catch (e) {
      message.error((e && e.message) || 'Failed to calculate reports');
      try {
        const params = { year, month };
        if (unitId) params.unit = unitId;
        else params.property = propertyId;
        if (servicePackageId) params.servicePackage = servicePackageId;
        setRows((await listReports(hostId, params)) || []);
      } catch (_err) {
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hostId) {
      setProperties([]);
      setPackages([]);
      setUnits([]);
      setRows([]);
      return;
    }
    setPropertiesLoading(true);
    Promise.all([loadHostProperties(hostId), listServicePackages(hostId)])
      .then(([propsList, pkgList]) => {
        const props = propsList || [];
        setProperties(props);
        setPackages(pkgList || []);
        setFilters(prev => {
          if (prev.propertyId && props.some(p => String(p._id) === String(prev.propertyId))) {
            return prev;
          }
          return {
            ...prev,
            propertyId: props[0] ? String(props[0]._id) : undefined,
            servicePackageId: undefined,
            unitId: undefined,
          };
        });
      })
      .catch(() => {
        setProperties([]);
        setPackages([]);
        setFilters(prev => ({ ...prev, propertyId: undefined, servicePackageId: undefined, unitId: undefined }));
      })
      .finally(() => setPropertiesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostId]);

  useEffect(() => {
    if (!filters.propertyId) {
      setUnits([]);
      setUnitsLoading(false);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    loadUnitsForProperty(filters.propertyId)
      .then(list => {
        if (cancelled) return;
        const unitList = list || [];
        setUnits(unitList);
        setUnitNameById(prev => {
          const next = { ...prev };
          unitList.forEach(u => {
            next[String(u._id)] = u.name || String(u._id);
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) setUnits([]);
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.propertyId]);

  const propertyUnitIds = useMemo(() => (units || []).map(u => u && u._id), [units]);
  const scopedPackages = useMemo(() => packagesForProperty(packages, propertyUnitIds), [packages, propertyUnitIds]);
  const selectedPackage = useMemo(
    () => findPackageById(scopedPackages, filters.servicePackageId),
    [scopedPackages, filters.servicePackageId]
  );
  const unitOptions = useMemo(() => unitsForPackage(units, selectedPackage), [units, selectedPackage]);
  const packagesSelectLoading = !!(filters.propertyId && (propertiesLoading || unitsLoading));

  useEffect(() => {
    if (!filters.propertyId || unitsLoading || propertiesLoading) return;
    setFilters(prev => {
      const nextPackage = findPackageById(scopedPackages, prev.servicePackageId);
      const nextUnits = unitsForPackage(units, nextPackage);
      let servicePackageId = prev.servicePackageId;
      let unitId = prev.unitId;
      if (servicePackageId && !nextPackage) {
        servicePackageId = undefined;
        unitId = undefined;
      } else if (unitId && !(nextUnits || []).some(u => String(u._id) === String(unitId))) {
        unitId = undefined;
      }
      if (servicePackageId === prev.servicePackageId && unitId === prev.unitId) return prev;
      return Object.assign({}, prev, { servicePackageId, unitId });
    });
  }, [filters.propertyId, unitsLoading, propertiesLoading, units, scopedPackages]);

  const set = key => val => setFilters(prev => ({ ...prev, [key]: val }));

  const onMonthChange = date => {
    const m = toMonthDate(date);
    setFilters(prev => ({
      ...prev,
      monthDate: m,
      year: m.year(),
      month: m.month() + 1,
    }));
  };

  useEffect(() => {
    if (!hostId || !filters.propertyId) {
      setRows([]);
      return undefined;
    }
    load(filters.year, filters.month, filters.propertyId, filters.unitId, filters.servicePackageId);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.year, filters.month, filters.propertyId, filters.servicePackageId, filters.unitId, hostId, refreshTick]);

  useSyncPageFilters(history, location, {
    property: filters.propertyId,
    servicePackage: filters.servicePackageId,
    unit: filters.unitId,
    date: filters.monthDate.format(MONTH_FORMAT),
    view: filters.view,
  });

  const onAudit = async record => {
    try {
      await auditReporting(hostId, { unitId: record.unit, year: record.year, month: record.month });
      message.success('Audited');
      load(record.year, record.month, filters.propertyId, filters.unitId, filters.servicePackageId);
    } catch (e) {
      message.error('Audit failed');
    }
  };

  const onUnaudit = async record => {
    try {
      await unauditReporting(hostId, { unitId: record.unit, year: record.year, month: record.month });
      message.success('Unaudited');
      load(record.year, record.month, filters.propertyId, filters.unitId, filters.servicePackageId);
    } catch (e) {
      message.error('Unaudit failed');
    }
  };

  const onPay = async record => {
    try {
      await setPaymentStatus(hostId, { reportIds: [record._id], paymentStatus: 'paid' });
      message.success('Marked paid');
      load(record.year, record.month, filters.propertyId, filters.unitId, filters.servicePackageId);
    } catch (e) {
      message.error('Payment update failed');
    }
  };

  const onUnpay = async record => {
    try {
      await setPaymentStatus(hostId, { reportIds: [record._id], paymentStatus: 'unpaid', paidDate: 0 });
      message.success('Marked unpaid');
      load(record.year, record.month, filters.propertyId, filters.unitId, filters.servicePackageId);
    } catch (e) {
      message.error('Payment update failed');
    }
  };

  const isAudited = record => String(record && record.auditStatus) === 'audited';
  const isPaid = record => String(record && record.paymentStatus) === 'paid';

  const ownerNameOf = r => {
    const data = (r && r.ownedByData) || {};
    const populated = r && r.ownedBy && typeof r.ownedBy === 'object' ? r.ownedBy : null;
    return (
      (r && r.ownerName) ||
      data.name ||
      data.fullName ||
      (populated && (populated.name || [populated.firstName, populated.lastName].filter(Boolean).join(' '))) ||
      ''
    );
  };

  const selectedProperty = (properties || []).find(p => String(p._id) === String(filters.propertyId));
  const selectedPropertyName = (selectedProperty && selectedProperty.name) || '';
  const reportCards = (rows || []).map(r => {
    const listingUnit = (units || []).find(u => String(u && u._id) === String(r.unit));
    const maxFreeStay =
      (r.unitData && r.unitData.maxFreeStay) != null
        ? r.unitData.maxFreeStay
        : r.maxFreeStay != null
          ? r.maxFreeStay
          : listingUnit && listingUnit.maxFreeStay;
    return mapReportToCard(
      Object.assign({}, r, {
        unitName: unitNameById[String(r.unit)] || r.unitName,
        propertyName: r.propertyName || selectedPropertyName,
        ownerName: ownerNameOf(r),
        maxFreeStay,
        unitData: Object.assign({}, r.unitData, { maxFreeStay }),
      })
    );
  });

  const onPdf = async record => {
    const key = record._id || `${record.unit}-${record.year}-${record.month}`;
    setPdfLoadingId(key);
    const hide = message.loading('Generating PDF…', 0);
    try {
      const result = await getReportPdf(hostId, { unitId: record.unit, year: record.year, month: record.month });
      if (!result || !result.blob) {
        throw new Error('Empty PDF response');
      }
      const url = URL.createObjectURL(result.blob);
      const opened = window.open(url, '_blank');
      if (!opened) {
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename || 'owner-report.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      message.error((e && e.message) || 'PDF failed');
    } finally {
      hide();
      setPdfLoadingId(null);
    }
  };

  const onExport = () => {
    if (!hostId || !filters.propertyId) {
      message.warning('Select a property first');
      return;
    }
    const csvRows = mapOwnerHostProfitCsvRows(
      (rows || []).map(r =>
        Object.assign({}, r, {
          unitName: unitNameById[String(r.unit)] || r.unitName,
          propertyName: r.propertyName || selectedPropertyName,
          ownerName: ownerNameOf(r),
        })
      ),
      { units, propertyName: selectedPropertyName }
    );
    if (!csvRows.length) {
      message.warning('No reports to export');
      return;
    }
    const monthStr = Number(filters.month) < 10 ? `0${Number(filters.month)}` : String(filters.month);
    downloadCsvFile(`${filters.year}_${monthStr}_${selectedPropertyName}__Owner Host Profit.csv`, csvRows);
  };

  return (
    <Card title="Owner Reporting — Manage Reporting">
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} type="flex" align="bottom">
          <Col xs={24} sm={12} md={12} xl={6}>
            <Form.Item label="Property" style={{ marginBottom: 0 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={propertiesLoading ? 'Loading properties...' : 'Select property'}
                value={filters.propertyId}
                onChange={val =>
                  setFilters(prev => ({
                    ...prev,
                    propertyId: val,
                    servicePackageId: undefined,
                    unitId: undefined,
                  }))
                }
                disabled={!hostId}
                loading={propertiesLoading}
              >
                {properties.map(p => (
                  <Select.Option key={p._id} value={p._id}>
                    {p.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} xl={6}>
            <Form.Item label="Service Package" style={{ marginBottom: 0 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={
                  !filters.propertyId
                    ? 'Select property first'
                    : packagesSelectLoading
                    ? 'Loading packages...'
                    : 'All packages in property'
                }
                value={packagesSelectLoading ? undefined : filters.servicePackageId}
                onChange={val =>
                  setFilters(prev => {
                    const pkg = findPackageById(scopedPackages, val);
                    const nextUnits = unitsForPackage(units, pkg);
                    const unitOk = prev.unitId && nextUnits.some(u => String(u._id) === String(prev.unitId));
                    return {
                      ...prev,
                      servicePackageId: val || undefined,
                      unitId: unitOk ? prev.unitId : undefined,
                    };
                  })
                }
                disabled={!filters.propertyId || packagesSelectLoading}
                loading={packagesSelectLoading}
                suffixIcon={packagesSelectLoading ? <Icon type="loading" /> : undefined}
                notFoundContent={packagesSelectLoading ? 'Loading...' : 'No packages for this property'}
              >
                {scopedPackages.map(pkg => (
                  <Select.Option key={pkg._id} value={pkg._id}>
                    {pkg.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} xl={6}>
            <Form.Item label="Unit" style={{ marginBottom: 0 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={
                  unitsLoading
                    ? 'Loading units...'
                    : filters.propertyId
                    ? filters.servicePackageId
                      ? 'All units in package'
                      : 'All units in property'
                    : 'Select property first'
                }
                value={unitsLoading ? undefined : filters.unitId}
                onChange={set('unitId')}
                disabled={!filters.propertyId || unitsLoading}
                loading={unitsLoading}
                notFoundContent={unitsLoading ? 'Loading...' : 'No units for this property'}
              >
                {unitOptions.map(u => (
                  <Select.Option key={u._id} value={u._id}>
                    {u.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} xl={6}>
            <Form.Item label="Month" style={{ marginBottom: 0 }}>
              <MonthPicker defaultValue={filters.monthDate} onChange={onMonthChange} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
              <div className={styles.toolbar}>
                <Button
                  type="primary"
                  icon="reload"
                  onClick={() => setRefreshTick(tick => tick + 1)}
                  loading={loading}
                  disabled={!hostId || !filters.propertyId || loading}
                >
                  Refresh
                </Button>
                <Button
                  icon="download"
                  disabled={!hostId || !filters.propertyId || !rows.length}
                  onClick={onExport}
                >
                  Download CSV
                </Button>
                <Button icon="audit" onClick={() => setBulkAuditVisible(true)} disabled={!hostId}>
                  Bulk Audit
                </Button>
                <Button icon="dollar" onClick={() => setBulkPaidVisible(true)} disabled={!hostId}>
                  Bulk Paid
                </Button>
                <Radio.Group
                  buttonStyle="solid"
                  value={filters.view || 'card'}
                  onChange={e => setFilters(prev => ({ ...prev, view: e.target.value }))}
                >
                  <Radio.Button value="table">
                    <Icon type="table" /> Table
                  </Radio.Button>
                  <Radio.Button value="card">
                    <Icon type="appstore" /> Cards
                  </Radio.Button>
                </Radio.Group>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Reports calculate automatically"
        description="Changing property, service package, unit, or month recalculates unaudited rows from live bookings using each package’s calculation method and start day. Audited reports stay locked until you unaudit. New and updated reservations also refresh unaudited reports."
      />

      {filters.view === 'card' ? (
        <Spin spinning={loading}>
          <Row type="flex">
            {reportCards.map((card, i) => (
              <Col key={String(card.id || card.unitId || i)} xs={24} sm={24} lg={12} xl={8} xxl={6}>
                <ReportingCard
                  card={card}
                  record={rows[i]}
                  pdfLoading={pdfLoadingId === (rows[i] && (rows[i]._id || `${rows[i].unit}-${rows[i].year}-${rows[i].month}`))}
                  onAudit={onAudit}
                  onUnaudit={onUnaudit}
                  onPay={onPay}
                  onUnpay={onUnpay}
                  onPdf={onPdf}
                />
              </Col>
            ))}
          </Row>
          {!loading && !reportCards.length && (
            <div style={{ textAlign: 'center', color: 'rgba(0,0,0,0.45)', padding: 24 }}>No Data</div>
          )}
        </Spin>
      ) : (
      <div className={styles.tableWrap}>
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={rows}
          scroll={{ x: 1200 }}
          columns={[
          {
            title: 'Unit',
            width: 120,
            render: r => unitNameById[String(r.unit)] || String(r.unit),
          },
          {
            title: 'Owner',
            width: 140,
            render: r => ownerNameOf(r) || '-',
          },
          {
            title: 'Service Package Name',
            width: 240,
            render: r => {
              const pkg = r.servicePackageData || {};
              return r.servicePackageName || pkg.name || '-';
            },
          },
          { title: 'Year', dataIndex: 'year', width: 72 },
          { title: 'Month', dataIndex: 'month', width: 80 },
          { title: 'Net Profit', dataIndex: 'netProfit', width: 110 },
          { title: 'Total Payout', dataIndex: 'amountTransferToOwner', width: 120 },
          { title: 'Audit', dataIndex: 'auditStatus', width: 100 },
          { title: 'Payment', dataIndex: 'paymentStatus', width: 100 },
          {
            title: 'Actions',
            key: 'actions',
            width: 240,
            render: r => (
              <span>
                {isAudited(r) ? (
                  <Button type="link" onClick={() => onUnaudit(r)}>
                    Unaudit
                  </Button>
                ) : (
                  <Button type="link" onClick={() => onAudit(r)}>
                    Audit
                  </Button>
                )}
                {isPaid(r) ? (
                  <Button type="link" onClick={() => onUnpay(r)}>
                    Mark Unpaid
                  </Button>
                ) : (
                  <Button type="link" onClick={() => onPay(r)}>
                    Mark Paid
                  </Button>
                )}
                <Button
                  type="link"
                  loading={pdfLoadingId === (r._id || `${r.unit}-${r.year}-${r.month}`)}
                  onClick={() => onPdf(r)}
                >
                  PDF
                </Button>
              </span>
            ),
          },
        ]}
      />
      </div>
      )}

      <BulkAuditWizard
        visible={bulkAuditVisible}
        hostId={hostId}
        properties={properties}
        propertiesLoading={propertiesLoading}
        propertyId={filters.propertyId}
        year={filters.year}
        month={filters.month}
        onClose={() => setBulkAuditVisible(false)}
        onSuccess={() => {
          setBulkAuditVisible(false);
          load(filters.year, filters.month, filters.propertyId, filters.unitId, filters.servicePackageId);
        }}
      />
      <BulkPaidWizard
        visible={bulkPaidVisible}
        hostId={hostId}
        properties={properties}
        propertiesLoading={propertiesLoading}
        propertyId={filters.propertyId}
        year={filters.year}
        month={filters.month}
        onClose={() => setBulkPaidVisible(false)}
        onSuccess={() => {
          setBulkPaidVisible(false);
          load(filters.year, filters.month, filters.propertyId, filters.unitId, filters.servicePackageId);
        }}
      />
    </Card>
  );
};

export default withRouter(ReportingPage);
